import React, { useState } from 'react';
import { Card, Row, Col, Alert, Tag, Modal, Button, List } from 'antd';
import { motion } from 'framer-motion';
import { 
  WarningOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';

// const { Panel } = Collapse;

interface AntiDumpingCategory {
  id: number;
  category: string;
  products: string[];
  icon: React.ReactNode;
  color: string;
  riskLevel: 'high' | 'medium' | 'low';
}

const antiDumpingProducts: AntiDumpingCategory[] = [
  {
    id: 1,
    category: '钢铁金属及其制品',
    products: [
      '钢钉、钢管、碳合金钢丝条、金属垫、金刚石锯片、不锈钢加压管、定尺碳素钢板',
      '石油专用管材、冷轧碳钢、薄壁矩形钢管、熨衣架及部件、螺纹钢筋、弹簧垫圈、非封闭内置弹簧部件',
      '不锈钢拉制深冲水槽、冷轧钢板、无缝碳钢和合金钢标准管、管线管和压力管、圆锥滚子轴承',
      '铸铁件、金属镁、镀锌板、重锻造手动工具（斧子、撬杠、锤子、锯头）、钢制高压气瓶'
    ],
    icon: <WarningOutlined />,
    color: '#f5222d',
    riskLevel: 'high'
  },
  {
    id: 2,
    category: '化学产品',
    products: [
      '己内酰胺（又称碳酸锂）、聚对苯二甲酸乙二醇膜片和条、聚乙烯醇、高锰酸钾',
      '三聚氰胺、天然猪鬃油漆刷及刷头'
    ],
    icon: <ExclamationCircleOutlined />,
    color: '#fa8c16',
    riskLevel: 'high'
  },
  {
    id: 3,
    category: '杂项制品',
    products: [
      '木制卧室家具、复合木地板、非晶硅织物、晶体硅光伏电池、小直径石墨电极'
    ],
    icon: <InfoCircleOutlined />,
    color: '#faad14',
    riskLevel: 'medium'
  },
  {
    id: 4,
    category: '机械电气设备及部件',
    products: [
      '滚球轴承、汽车挡风玻璃、刹车鼓与刹车转子'
    ],
    icon: <WarningOutlined />,
    color: '#fa541c',
    riskLevel: 'high'
  },
  {
    id: 5,
    category: '纸及纸制品',
    products: [
      '铜版纸、格记录纸、薄缩纸、折叠礼品盒、皱纹纸、无涂层纸'
    ],
    icon: <InfoCircleOutlined />,
    color: '#1890ff',
    riskLevel: 'medium'
  },
  {
    id: 6,
    category: '纺织制品',
    products: [
      '聚酯人造纤维、画布、编织电热毯'
    ],
    icon: <InfoCircleOutlined />,
    color: '#722ed1',
    riskLevel: 'medium'
  },
  {
    id: 7,
    category: '预制食品与饮料',
    products: [
      '港贡蘑菇罐头、柠檬酸与柠檬酸盐、华糖醇、冷冻的机罐装的暖水虾'
    ],
    icon: <ExclamationCircleOutlined />,
    color: '#eb2f96',
    riskLevel: 'high'
  },
  {
    id: 8,
    category: '塑料与橡胶制品',
    products: [
      '复合编织袋、聚乙烯手提袋、新充气工程机械轮胎'
    ],
    icon: <WarningOutlined />,
    color: '#52c41a',
    riskLevel: 'medium'
  },
  {
    id: 9,
    category: '矿石及矿物燃料',
    products: [
      '钻石铝条及部件'
    ],
    icon: <InfoCircleOutlined />,
    color: '#13c2c2',
    riskLevel: 'low'
  },
  {
    id: 10,
    category: '家用电器',
    products: [
      '彩色电视机、家用大型洗衣机'
    ],
    icon: <WarningOutlined />,
    color: '#fa8c16',
    riskLevel: 'high'
  },
  {
    id: 11,
    category: '车船运输设备及部件',
    products: [
      '自行车、手推车'
    ],
    icon: <InfoCircleOutlined />,
    color: '#2f54eb',
    riskLevel: 'medium'
  },
  {
    id: 12,
    category: '建材与玻璃制品',
    products: [
      '铝型材、双轴土工格栅、镁碳砖'
    ],
    icon: <WarningOutlined />,
    color: '#fa541c',
    riskLevel: 'medium'
  },
  {
    id: 13,
    category: '蔬菜产品',
    products: [
      '蜂蜜'
    ],
    icon: <InfoCircleOutlined />,
    color: '#fadb14',
    riskLevel: 'low'
  },
  {
    id: 14,
    category: '铅笔',
    products: [
      '铅笔'
    ],
    icon: <InfoCircleOutlined />,
    color: '#13c2c2',
    riskLevel: 'low'
  }
];

const AntiDumpingWarning: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<AntiDumpingCategory | null>(null);

  const handleCategoryClick = (category: AntiDumpingCategory) => {
    setSelectedCategory(category);
    setModalVisible(true);
  };

  const getRiskLevelColor = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high': return '#f5222d';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getRiskLevelText = (level: 'high' | 'medium' | 'low') => {
    switch (level) {
      case 'high': return '高风险';
      case 'medium': return '中风险';
      case 'low': return '低风险';
      default: return '未知';
    }
  };

  return (
    <section id="anti-dumping" style={{ 
      padding: '80px 0', 
      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 50%, #fecfef 100%)',
      position: 'relative'
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 className="display-medium" style={{ 
            marginBottom: '24px',
            color: '#722ed1'
          }}>
            美国反倾销产品警示
          </h2>
          <p className="body-large" style={{ 
            color: '#8c8c8c', 
            maxWidth: '800px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            以下产品类别涉及美国反倾销政策，发货前请仔细核实，避免贸易风险
          </p>
        </motion.div>

        {/* 重要提醒 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: '40px' }}
        >
          <Alert
            message="重要风险提醒"
            description="美国对以下14个大类产品实施反倾销政策，请在发货前务必核实产品是否涉及反倾销税收。如有疑问，请咨询专业报关顾问。"
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '2px solid #ff7875',
              borderRadius: '12px',
              fontSize: '16px'
            }}
          />
        </motion.div>

        {/* 产品类别网格 */}
        <Row gutter={[24, 24]}>
          {antiDumpingProducts.map((category, index) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={category.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  hoverable
                  onClick={() => handleCategoryClick(category)}
                  style={{
                    height: '220px',
                    borderRadius: '16px',
                    border: `2px solid ${category.color}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                  bodyStyle={{ 
                    padding: '20px', 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                  className="anti-dumping-card"
                >
                  <div>
                    <div style={{
                      fontSize: '48px',
                      color: category.color,
                      marginBottom: '12px'
                    }}>
                      {category.icon}
                    </div>
                    
                    <h4 style={{ 
                      color: '#1D1D1F',
                      marginBottom: '12px',
                      fontSize: '1rem',
                      fontWeight: '600',
                      lineHeight: '1.3'
                    }}>
                      {category.category}
                    </h4>
                  </div>
                  
                  <div>
                    <Tag 
                      color={getRiskLevelColor(category.riskLevel)} 
                      style={{ 
                        marginBottom: '8px',
                        fontWeight: '600'
                      }}
                    >
                      {getRiskLevelText(category.riskLevel)}
                    </Tag>
                    
                    <div style={{ 
                      color: '#666',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}>
                      <EyeOutlined />
                      点击查看详情
                    </div>
                  </div>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* 风险等级说明 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          style={{ marginTop: '60px' }}
        >
          <Card
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: 'none',
              borderRadius: '16px'
            }}
            bodyStyle={{ padding: '32px' }}
          >
            <h3 style={{ 
              textAlign: 'center', 
              marginBottom: '24px',
              color: '#1D1D1F',
              fontSize: '1.25rem',
              fontWeight: '600'
            }}>
              风险等级说明
            </h3>

            <Row gutter={[32, 16]} justify="center">
              <Col xs={24} sm={8} style={{ textAlign: 'center' }}>
                <Tag color="#f5222d" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  高风险
                </Tag>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                  严格管制，高额反倾销税
                </p>
              </Col>
              <Col xs={24} sm={8} style={{ textAlign: 'center' }}>
                <Tag color="#faad14" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  中风险
                </Tag>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                  部分产品受限，需要核实
                </p>
              </Col>
              <Col xs={24} sm={8} style={{ textAlign: 'center' }}>
                <Tag color="#52c41a" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  低风险
                </Tag>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                  一般性限制，影响较小
                </p>
              </Col>
            </Row>
          </Card>
        </motion.div>

        {/* 详细信息模态框 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {selectedCategory?.icon}
              <span>{selectedCategory?.category} 详细信息</span>
              <Tag color={selectedCategory ? getRiskLevelColor(selectedCategory.riskLevel) : '#d9d9d9'}>
                {selectedCategory ? getRiskLevelText(selectedCategory.riskLevel) : ''}
              </Tag>
            </div>
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setModalVisible(false)}>
              关闭
            </Button>
          ]}
          width={700}
        >
          {selectedCategory && (
            <div>
              <Alert
                message="涉及产品清单"
                description="以下产品可能涉及美国反倾销政策，发货前请务必核实具体税率和限制条件"
                type="warning"
                showIcon
                style={{ marginBottom: '20px' }}
              />
              
              <List
                size="small"
                bordered
                dataSource={selectedCategory.products}
                renderItem={(item, index) => (
                  <List.Item>
                    <div style={{ width: '100%' }}>
                      <strong style={{ color: selectedCategory.color }}>
                        {index + 1}.
                      </strong>
                      <span style={{ marginLeft: '8px', lineHeight: '1.5' }}>
                        {item}
                      </span>
                    </div>
                  </List.Item>
                )}
              />
              
              <Alert
                message="重要提醒"
                description="具体的反倾销税率和限制措施可能会发生变化，请在发货前咨询最新政策或联系我们的专业团队进行确认。"
                type="info"
                showIcon
                style={{ marginTop: '20px' }}
              />
            </div>
          )}
        </Modal>
      </div>

      {/* 背景装饰 */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '0%',
        width: '200px',
        height: '200px',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: 1
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '-5%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: 1
      }} />

      <style>{`
        .anti-dumping-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </section>
  );
};

export default AntiDumpingWarning;
